import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getQuota, listRecentRecipients, startCheckout } from '../api';

/**
 * The dashboard's "what do I have, and who do I send to" card.
 *
 * QuotaNotice (the compose-step nudge) deliberately renders NOTHING when a
 * sender is unlimited — it exists to warn about a cap. That left the good news
 * invisible: a practice on unlimited had no way to know sending was included
 * with their plan. This is the positive statement of the same fact, and it is
 * always present on the dashboard.
 *
 * It also carries "send again to", because the dashboard is where someone lands
 * when they intend to send to the same specialist as last time — the compose
 * step's chips only help once you are already composing.
 */

const TIER = {
  plan_bundled: {
    title: 'Unlimited sending',
    body: 'Included with Practice Autopilot and AI Dental Office — every file size, every recipient, no monthly count, and an email the moment a recipient downloads. Everyone at your office is covered.',
  },
  autopilot: {
    title: 'Unlimited sending',
    body: 'Included with your plan — every file size, every recipient, no monthly count, and an email the moment a recipient downloads.',
  },
  addon: {
    title: 'Unlimited sending',
    body: 'Your practice has the Send Unlimited add-on — every file size, every recipient, no monthly count, and an email the moment a recipient downloads.',
  },
  personal_plan: {
    title: 'Unlimited sending',
    body: 'Your $19.99/year plan is active — every file size, every recipient, no monthly count, and an email the moment a recipient downloads.',
  },
};

export default function PlanStatus() {
  const [quota, setQuota] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    getQuota().then((r) => setQuota(r.quota)).catch(() => setQuota(null));
    listRecentRecipients().then((r) => setRecent(r.recipients || [])).catch(() => setRecent([]));
  }, []);

  if (!quota) return null;

  const unlimited = quota.allowed && !Number.isFinite(quota.remaining);
  const tier = unlimited ? TIER[quota.reason] || TIER.personal_plan : null;
  const isPractice = quota.upgrade_kind === 'practice_addon';

  async function upgrade() {
    setBusy(true);
    setNotice(null);
    try {
      const r = await startCheckout();
      window.location.href = r.checkout_url;
    } catch (e) {
      setNotice(e.status === 409
        ? (e.data?.error || 'Unlimited sending comes with your practice plan.')
        : (e.message || 'Could not start checkout'));
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ background: unlimited ? '#f0fdfa' : '#f8fafc', border: `1px solid ${unlimited ? '#99f6e4' : '#e2e8f0'}` }}>
      <h2 style={{ marginTop: 0, fontSize: 17 }}>
        {unlimited
          ? tier.title
          : `${quota.remaining} of 5 free sends left this month`}
      </h2>
      <p className="muted" style={{ fontSize: 14, marginTop: -4, marginBottom: unlimited ? 0 : 12 }}>
        {unlimited
          ? tier.body
          : isPractice
            ? 'Unlimited sending is included with Practice Autopilot and AI Dental Office, or as a $14.99/year add-on for your whole office. Whoever handles billing can turn either on.'
            : 'Unlimited sending is $19.99 a year — every file size, every recipient, no monthly count.'}
      </p>

      {!unlimited && !isPractice && !notice && (
        <button className="btn btn-primary" onClick={upgrade} disabled={busy}>
          {busy ? 'Opening checkout…' : 'Go unlimited — $19.99/year'}
        </button>
      )}
      {notice && <p style={{ margin: '8px 0 0', fontSize: 14 }}>{notice}</p>}

      {recent.length > 0 && (
        <div style={{ marginTop: unlimited ? 14 : 16, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
          <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>Send again to</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {recent.map((r) => (
              <Link
                key={r.email}
                className="btn btn-secondary"
                // Lands on the compose form with this recipient already filled.
                to={`/?to=${encodeURIComponent(r.email)}${r.name ? `&name=${encodeURIComponent(r.name)}` : ''}`}
                title={r.email}
                style={{ padding: '5px 11px', fontSize: 13, maxWidth: '100%', textDecoration: 'none' }}
              >
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.name || r.email}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
